import { recordRuntimeError } from '../../lib/runtimeErrors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppRequestSchema } from '@codebox/shared';
import { api } from '../../lib/api';
import { onFrame, sendFrame } from '../../lib/socket';
import type { StudioTool } from './StudioToolRail';

type Device = 'desktop' | 'tablet' | 'mobile';

/** A DOM element the learner picked with the Select tool — becomes the target of the next AI prompt. */
export interface SelectedElement {
  tag: string;
  id: string;
  cls: string;
  text: string;
}

const DEVICE_WIDTH: Record<Device, number | null> = {
  desktop: null,
  tablet: 768,
  mobile: 390,
};

/** Client-side per-method rate caps — defense in depth on top of the server's own limits. */
const CLIENT_LIMITS: Record<string, number> = { 'ai.text': 12, 'ai.image': 4, 'data.op': 60, 'auth.op': 30, 'assets.get': 30, export: 10, 'realtime.op': 40 };

/*
 * Mobile-style scrollbars for the emulated app: thin, rounded, translucent, no track — desktop's
 * fat gray bars inside a phone bezel break the illusion instantly. Injected into the DOCUMENT
 * rather than toggled per device because the iframe is an opaque origin (no contentDocument access)
 * and swapping srcDoc on a device switch would reload the app and wipe its state. Always-on is the
 * right trade: thin scrollbars read as native on mobile/tablet and simply modern on desktop.
 */
const PREVIEW_SCROLLBARS = `<style id="cbx-preview-scrollbars">
*{scrollbar-width:thin;scrollbar-color:rgba(100,116,139,.5) transparent}
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:rgba(100,116,139,.5);border-radius:999px}
::-webkit-scrollbar-corner{background:transparent}
</style>`;

function withPreviewChrome(html: string): string {
  const openHead = /<head[^>]*>/i.exec(html);
  if (openHead) return html.replace(openHead[0], `${openHead[0]}${PREVIEW_SCROLLBARS}`);
  return PREVIEW_SCROLLBARS + html;
}

interface PreviewPaneProps {
  projectId: string;
  html: string | null;
  loading: boolean;
  onReload: () => void;
  /** studio owns the device toggle + frame, so render only the iframe */
  chromeless?: boolean;
  /** third crash in a row — Sparky turns it into a learning moment (D8) */
  onEscalate: (plain: string, nudge: string) => void;
  /** a free auto-fix job was queued */
  onAutofix: () => void;
  /** which workspace tool is active (35g) — select / grab / text drive the in-preview overlay */
  tool?: StudioTool;
  /** the Select tool picked (or cleared) an element — studio shows it as the AI prompt target */
  onSelectElement?: (el: SelectedElement | null) => void;
  /** a free text edit was saved; studio can toast it */
  onTextEdit?: (ok: boolean) => void;
  /** the Snapshot tool captured the current preview (null = capture failed) */
  onSnapshot?: (dataUrl: string | null) => void;
  /** grab-tool drag the app's own document could not absorb — pan the workspace by this much */
  onPan?: (dx: number, dy: number) => void;
  /** a media tile is being dragged from the Media panel (35h) — arms the in-app drop targets */
  draggingMedia?: { kind: 'image' | 'video' | 'audio'; url: string } | null;
  /** a media tile was dropped onto an app element and the swap was applied (or failed) */
  onMediaDropped?: (ok: boolean) => void;
}

/**
 * Renders the generated app AND brokers its `CodeBox.*` bridge (spec §10.2 layers 1+3).
 *
 * Layer 1: `sandbox="allow-scripts"` without `allow-same-origin` — the app runs in an opaque origin
 * and can never touch studio cookies/DOM. Layer 3 is this component: every postMessage from the app
 * is zod-validated against the cbx:v1 schema, rate-limited per method, and forwarded to the API with
 * the STUDIO's auth — a credential never enters the app document.
 */
export function PreviewPane({ projectId, html, loading, onReload, chromeless, onEscalate, onAutofix, tool = 'select', onSelectElement, onTextEdit, onSnapshot, onPan, draggingMedia, onMediaDropped }: PreviewPaneProps) {
  const [device, setDevice] = useState<Device>('desktop');
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [runtimeErrors, setRuntimeErrors] = useState<string[]>([]);
  const rateRef = useRef<Map<string, { until: number; count: number }>>(new Map());
  const reportedErrors = useRef<Set<string>>(new Set());
  const realtimeWaiters = useRef<Array<(info: { code: string }) => void>>([]);

  useEffect(() => {
    setRuntimeErrors([]);
    reportedErrors.current.clear();
  }, [html]);

  const overLimit = (type: string): boolean => {
    const limit = CLIENT_LIMITS[type] ?? 30;
    const now = Date.now();
    const entry = rateRef.current.get(type);
    if (!entry || entry.until < now) {
      rateRef.current.set(type, { until: now + 60_000, count: 1 });
      return false;
    }
    entry.count += 1;
    return entry.count > limit;
  };

  const reply = useCallback((id: string, ok: boolean, result?: unknown, message?: string) => {
    frameRef.current?.contentWindow?.postMessage(
      { v: 'cbx:v1', type: 'response', id, ok, result, error: ok ? undefined : { code: 'bridge', message: message ?? 'That did not work' } },
      '*',
    );
  }, []);

  const pushToApp = useCallback((payload: unknown) => {
    frameRef.current?.contentWindow?.postMessage({ v: 'cbx:v1', type: 'realtime.push', id: 'push', payload }, '*');
  }, []);

  /* --------------------------- workspace editor tools (35g) --------------------------- */
  // Keep the latest tool in a ref so the iframe's onLoad can re-arm the overlay after a reload,
  // which resets the injected script back to 'none'.
  const toolRef = useRef<StudioTool>(tool);
  toolRef.current = tool;
  const postTool = useCallback((t: StudioTool) => {
    frameRef.current?.contentWindow?.postMessage({ v: 'cbx:v1', type: 'editor.tool', payload: { tool: t } }, '*');
  }, []);
  useEffect(() => {
    postTool(tool);
  }, [tool, postTool]);

  /*
   * A grab-drag released OUTSIDE the iframe (over the studio chrome, or off the window entirely) is
   * a release the app document may never see, leaving the grab tool stuck panning with no button
   * held. The parent always sees it, so it tells the frame to let go.
   */
  useEffect(() => {
    const release = () =>
      frameRef.current?.contentWindow?.postMessage({ v: 'cbx:v1', type: 'editor.pan-end' }, '*');
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', release);
    };
  }, []);

  // selection + inline text edits coming back from the overlay
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { v?: string; type?: string; payload?: Record<string, unknown> };
      if (data?.v !== 'cbx:v1') return;
      if (data.type === 'editor.selected') {
        onSelectElement?.(data.payload as unknown as SelectedElement);
      } else if (data.type === 'editor.deselected') {
        onSelectElement?.(null);
      } else if (data.type === 'editor.pan') {
        const { dx, dy } = (data.payload ?? {}) as { dx?: number; dy?: number };
        if (typeof dx === 'number' && typeof dy === 'number') onPan?.(dx, dy);
      } else if (data.type === 'editor.text-edit') {
        const before = data.payload?.before;
        const after = data.payload?.after;
        if (typeof before !== 'string' || typeof after !== 'string') return;
        void api
          .post(`/box/projects/${projectId}/media/text/replace`, { before, after })
          .then(() => {
            onTextEdit?.(true);
            onReload(); // resync the preview with the saved content
          })
          .catch(() => {
            onTextEdit?.(false);
            onReload(); // reload reverts the optimistic in-place edit
          });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [projectId, onSelectElement, onTextEdit, onReload, onPan]);

  // Snapshot tool (35g): when it becomes active, ask the bundle for a picture of the current preview.
  const lastTool = useRef<StudioTool>(tool);
  useEffect(() => {
    const wasSnapshot = lastTool.current === 'snapshot';
    lastTool.current = tool;
    if (tool !== 'snapshot' || wasSnapshot || !onSnapshot) return;
    const id = `snap-${Date.now().toString(36)}`;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { v?: string; type?: string; id?: string; payload?: { dataUrl?: string } };
      if (data?.v !== 'cbx:v1' || data.type !== 'snapshot-page-result' || data.id !== id) return;
      window.removeEventListener('message', onMessage);
      onSnapshot(data.payload?.dataUrl ?? null);
    };
    window.addEventListener('message', onMessage);
    frameRef.current?.contentWindow?.postMessage({ v: 'cbx:v1', type: 'snapshot-page-request', id }, '*');
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      onSnapshot(null); // capture never came back — let the studio recover
    }, 8000);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeout);
    };
  }, [tool, onSnapshot]);

  // Media drag-and-drop (35h): tell the in-app drop handler when a tile is being dragged and of what kind.
  const dragUrlRef = useRef<string | null>(null);
  dragUrlRef.current = draggingMedia?.url ?? null;
  useEffect(() => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    if (draggingMedia) win.postMessage({ v: 'cbx:v1', type: 'media.drag', payload: { kind: draggingMedia.kind } }, '*');
    else win.postMessage({ v: 'cbx:v1', type: 'media.drag-end' }, '*');
  }, [draggingMedia]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { v?: string; type?: string; payload?: { kind?: string; src?: string } };
      if (data?.v !== 'cbx:v1' || data.type !== 'media.dropped') return;
      const src = data.payload?.src;
      const url = dragUrlRef.current;
      if (!src || !url) return;
      void api
        .post(`/box/projects/${projectId}/media/replace`, { currentSrc: src, url })
        .then(() => {
          onMediaDropped?.(true);
          onReload();
        })
        .catch(() => onMediaDropped?.(false));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [projectId, onMediaDropped, onReload]);

  /* ------------------------- app-room frames from the studio socket ------------------------- */
  useEffect(() => {
    return onFrame(frame => {
      if (frame.t === 'app-joined') {
        const waiter = realtimeWaiters.current.shift();
        waiter?.({ code: String(frame.code) });
        pushToApp(frame);
      } else if (frame.t === 'app-message' || frame.t === 'app-state' || frame.t === 'app-presence' || frame.t === 'app-error') {
        pushToApp(frame);
      }
    });
  }, [pushToApp]);

  /* --------------------------- crash reporting → free auto-fix loop -------------------------- */
  const handleCrash = useCallback(
    async (message: string, stack?: string) => {
      setRuntimeErrors(prev => (prev.includes(message) ? prev : [...prev.slice(-4), message]));
      // one auto-fix per distinct error per preview load — a crash loop must not spam jobs
      if (reportedErrors.current.has(message)) return;
      reportedErrors.current.add(message);
      try {
        const result = await api.post<{ escalate: boolean; plain: string; nudge?: string }>(
          `/box/projects/${projectId}/autofix`,
          { message, stack },
        );
        if (result.escalate) onEscalate(result.plain, result.nudge ?? 'Tell me what should happen instead.');
        else onAutofix();
      } catch {
        /* autofix is best-effort */
      }
    },
    [projectId, onEscalate, onAutofix],
  );

  /*
   * 35c layer 2: the layout sentry inside the bundle reports "runs fine but looks broken" with
   * measured pixels. Same free-fix rail as crashes; deduped per defect kind per preview load.
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { v?: string; type?: string; payload?: { kind?: string; facts?: unknown } };
      if (data?.v !== 'cbx:v1' || data.type !== 'layout-defect' || !data.payload?.kind) return;
      const key = `layout:${data.payload.kind}`;
      if (reportedErrors.current.has(key)) return;
      reportedErrors.current.add(key);
      void api
        .post<{ escalate: boolean; plain: string; nudge?: string }>(`/box/projects/${projectId}/autofix`, {
          message: `Layout problem detected: ${data.payload.kind}`,
          kind: 'layout',
          details: data.payload.facts,
        })
        .then(result => {
          if (result.escalate) onEscalate(result.plain, result.nudge ?? 'Tell me what it should look like.');
          else onAutofix();
        })
        .catch(() => undefined);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [projectId, onEscalate, onAutofix]);

  /* --------------------------------- the bridge broker itself -------------------------------- */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;

      const parsed = AppRequestSchema.safeParse(event.data);
      if (!parsed.success) return;
      const message = parsed.data;

      switch (message.type) {
        case 'bridges-ready':
          return;
        case 'runtime-error':
          recordRuntimeError(message.payload.message, message.payload.stack);
          void handleCrash(message.payload.message, message.payload.stack);
          return;
        case 'navigate-attempt':
          return; // blocked inside the app already; nothing to do
        case 'realtime.op': {
          if (overLimit('realtime.op')) return reply(message.id, false, undefined, 'Take a short break');
          const op = message.payload.op;
          if (op === 'join') {
            realtimeWaiters.current.push(info => reply(message.id, true, info));
            const code = message.payload.room === 'new' ? undefined : message.payload.room;
            sendFrame({ t: 'app-join', projectId, code });
          } else if (op === 'send') {
            sendFrame({ t: 'app-send', message: message.payload.message });
            reply(message.id, true, { sent: true });
          } else if (op === 'presence') {
            reply(message.id, true, []); // presence rides the push frames
          } else if (op === 'leave') {
            sendFrame({ t: 'app-leave' });
            reply(message.id, true, { left: true });
          }
          return;
        }
        default: {
          // handler-style realtime calls come through as realtime.op message.name via room.handler
          if (overLimit(message.type)) return reply(message.id, false, undefined, 'Take a short break — too many requests');
          void api
            .post<{ ok: boolean; result?: unknown; error?: { message: string } }>(
              `/box/projects/${projectId}/bridge`,
              { type: message.type, payload: message.payload },
            )
            .then(body => reply(message.id, true, (body as { result?: unknown }).result))
            .catch(err => reply(message.id, false, undefined, err instanceof Error ? err.message : undefined));
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [projectId, reply, handleCrash]);

  // relay handler calls sent as app-handler through realtime.op message payloads
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { v?: string; type?: string; id?: string; payload?: { op?: string; message?: { name?: string; args?: unknown } } };
      if (data?.v === 'cbx:v1' && data.type === 'realtime.op' && data.payload?.op === 'handler' && data.id) {
        sendFrame({ t: 'app-handler', name: data.payload.message?.name, args: data.payload.message?.args });
        reply(data.id, true, { called: true });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [reply]);

  const width = DEVICE_WIDTH[device];
  const frameStyle = useMemo(
    () => (width ? { width: `${width}px`, maxWidth: '100%' } : { width: '100%' }),
    [width],
  );

  /*
   * Chromeless mode: the studio owns the device toggle (toolbar) and the frame (DeviceFrame), so the
   * pane renders only the iframe. The standalone chrome below is kept for any surface that embeds
   * the preview on its own.
   */
  const frame = loading ? (
    <div className="skeleton h-full w-full" />
  ) : (
    <iframe
      ref={frameRef}
      title="App preview"
      // TEMP QUICK PATCH (camera/peripherals): allow-same-origin gives this srcDoc frame a real
      // origin so getUserMedia (camera/mic) works, and the `allow` attr delegates the
      // Permissions-Policy features to it. SECURITY DEBT — this makes the generated app same-origin
      // with the studio, so it can now reach studio cookies/DOM. TEMPORARY: the real fix is to serve
      // the preview from a separate origin (BOX_APPS_ORIGIN model, services/box-api/src/routes/appsOrigin.ts).
      // Do NOT treat this as the long-term solution.
      sandbox="allow-scripts allow-same-origin"
      allow="camera; microphone; display-capture; geolocation"
      srcDoc={html ? withPreviewChrome(html) : ''}
      onLoad={() => postTool(toolRef.current)}
      style={chromeless ? undefined : frameStyle}
      className={chromeless ? 'h-full w-full border-0 bg-white' : 'h-full min-h-[420px] rounded-xl border border-line bg-white'}
    />
  );

  if (chromeless) {
    return (
      <div className="relative h-full w-full">
        {frame}
        {runtimeErrors.length > 0 && (
          <div className="absolute inset-x-0 bottom-0 border-t border-line bg-surface/95 px-3 py-2 backdrop-blur">
            <p className="text-xs font-bold">Something went wrong while running your app</p>
            <p className="truncate font-mono text-[11px] text-muted">{runtimeErrors[runtimeErrors.length - 1]}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl bg-page p-[3px]">
            {(['desktop', 'tablet', 'mobile'] as const).map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setDevice(d)}
                className={[
                  'rounded-lg px-2.5 py-1 text-xs font-semibold capitalize transition',
                  device === d ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
                ].join(' ')}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onReload}
          className="rounded-lg px-2.5 py-1 text-xs font-semibold text-muted transition hover:bg-page hover:text-ink"
        >
          Reload
        </button>
      </div>

      <div className="grid min-h-0 flex-1 place-items-start justify-center overflow-auto bg-page p-4">{frame}</div>

      {runtimeErrors.length > 0 && (
        <div className="border-t border-line bg-page px-3 py-2">
          <p className="text-xs font-bold">Something went wrong while running your app</p>
          <ul className="mt-1 space-y-0.5">
            {runtimeErrors.map((message, i) => (
              <li key={i} className="truncate font-mono text-xs text-muted">
                {message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
